import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * Tabs controller for the locations section.
 *
 * @extends {Component}
 */
class LocationsTabsComponent extends Component {
  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement[]} */
  #mobileWrappers = [];

  /** @type {HTMLElement[]} */
  #desktopWrappers = [];

  /** @type {HTMLElement | null} */
  #background = null;

  /** @type {ReturnType<typeof debounce>} */
  #handleResize = debounce(() => {
    this.#updateBackgroundPosition();
  }, 100);

  connectedCallback() {
    super.connectedCallback();

    this.#cacheElements();
    if (!this.#tablist || this.#tabs.length === 0) return;

    this.#setInitialState();
    this.#updateBackgroundPosition();

    if (this.#background && prefersReducedMotion()) {
      this.#background.style.transition = 'none';
    }

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('keydown', this.#onTabKeydown);
    }

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#onTabClick);
      tab.removeEventListener('keydown', this.#onTabKeydown);
    }

    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize.cancel();
  }

  #cacheElements() {
    this.#tablist = this.querySelector('[data-tablist]');
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.#mobileWrappers = Array.from(this.querySelectorAll('.locations-section__mobile-image-wrapper'));
    this.#desktopWrappers = Array.from(this.querySelectorAll('.locations-section__desktop-image-wrapper'));
    this.#background = this.querySelector('.locations-section__tabs-background');
  }

  #setInitialState() {
    const activeIndex = this.#getActiveTabIndex();
    this.#switchTab(activeIndex, { focus: false });
  }

  #getActiveTabIndex() {
    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const activeIndex = activeTab ? this.#tabs.indexOf(activeTab) : 0;

    return activeIndex >= 0 ? activeIndex : 0;
  }

  /**
   * @param {number} tabIndex
   * @param {{ focus?: boolean }} [options]
   */
  #switchTab(tabIndex, options = {}) {
    if (tabIndex < 0 || tabIndex >= this.#tabs.length) return;

    const { focus = true } = options;
    const activeTabNumber = tabIndex + 1;

    for (const [index, tab] of this.#tabs.entries()) {
      const isSelected = index === tabIndex;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');

      if (focus && isSelected) {
        tab.focus();
      }
    }

    for (const panel of this.#panels) {
      const panelId = panel.id;
      const panelTab = this.#tabs.find((tab) => tab.getAttribute('aria-controls') === panelId);
      const isSelected = panelTab ? panelTab.getAttribute('aria-selected') === 'true' : false;

      panel.toggleAttribute('hidden', !isSelected);
    }

    this.#setActiveImages(activeTabNumber);
    this.#updateBackgroundPosition();
  }

  /**
   * @param {number} tabNumber
   */
  #setActiveImages(tabNumber) {
    for (const wrapper of this.#mobileWrappers) {
      const isTabOne = wrapper.classList.contains('locations-section__mobile-image-wrapper--tab-1');
      wrapper.classList.toggle('active', tabNumber === (isTabOne ? 1 : 2));
    }

    for (const wrapper of this.#desktopWrappers) {
      const isTabOne = wrapper.classList.contains('locations-section__desktop-image-wrapper--tab-1');
      wrapper.classList.toggle('active', tabNumber === (isTabOne ? 1 : 2));
    }
  }

  #updateBackgroundPosition() {
    if (!this.#background || !this.#tablist) return;

    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!activeTab) return;

    const tabRect = activeTab.getBoundingClientRect();
    const tabsRect = this.#tablist.getBoundingClientRect();
    const leftOffset = tabRect.left - tabsRect.left;

    this.#background.style.transform = `translateX(${leftOffset}px)`;
    this.#background.style.width = `${tabRect.width}px`;
  }

  /**
   * @param {MouseEvent} event
   */
  #onTabClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const tabIndex = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (tabIndex < 0) return;

    this.#switchTab(tabIndex);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onTabKeydown = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const currentIndex = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (currentIndex < 0) return;

    let targetIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        event.preventDefault();
        targetIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = this.#tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#switchTab(currentIndex);
        return;
      default:
        return;
    }

    this.#switchTab(targetIndex);
  };
}

customElements.define('locations-tabs-component', LocationsTabsComponent);
